import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/pro-light-svg-icons';
import { ColorPalette } from '../../../../../components/config';
import { attendanceAreaPathName, getCitizenLessonDeviationEditPath } from '../../../attendance-area-pathnames';
import { CitizenLessonAttendanceState } from '../interfaces';
import { formatCheckInTime } from './attendance-time';
import { formatDate } from '../../../../../utilities';
import { useFeatureToggles } from '../../../../../feature-toggles';

interface IAttendanceSpecialConditionsWarningProps {
  citizenLessonAttendance: CitizenLessonAttendanceState;
}

export function buildSpecialConditionNotes(citizenLessonAttendance: CitizenLessonAttendanceState, showAbsenceComment: boolean): string[] {
  const { hasLessonDeviation, lessonDeviationBeginAt, lessonDeviationEndAt, interviews, absences } = citizenLessonAttendance;
  const notes: string[] = [];

  if (hasLessonDeviation) {
    const meetingTime = lessonDeviationBeginAt && lessonDeviationEndAt ? `: Mødetid ${formatCheckInTime(lessonDeviationBeginAt)}-${formatCheckInTime(lessonDeviationEndAt)}` : '';
    notes.push(`Borger har særplan${meetingTime}`);
  }

  interviews?.forEach(interview => {
    notes.push(`Borger har en ${interview.interviewTypeName} i samme tidsrum: ${formatCheckInTime(interview.startDate)}-${formatCheckInTime(interview.endDate)}`);
  });

  absences?.forEach(absence => {
    const period = absence.endDate ? `${formatDate(absence.startDate)} - ${formatDate(absence.endDate)}` : `fra ${formatDate(absence.startDate)}`;
    const comment = showAbsenceComment && absence.comment ? `. Kommentar: ${absence.comment}` : '';
    notes.push(`Borger har registreret fravær i samme tidsrum: ${absence.absenceTypeName}: ${period}${comment}`);
  });

  return notes;
}

export function AttendanceSpecialConditionsWarning({ citizenLessonAttendance }: IAttendanceSpecialConditionsWarningProps) {
  const { citizenId, citizenLessonDeviationId } = citizenLessonAttendance;
  const { isFeatureEnabled } = useFeatureToggles();
  const notes = buildSpecialConditionNotes(citizenLessonAttendance, isFeatureEnabled('EnableAbsenceCommentInAttendanceProtocol'));

  if (notes.length === 0) {
    return null;
  }

  function handleOpenDeviation(event: React.MouseEvent<HTMLElement>) {
    event.stopPropagation();
    if (citizenLessonDeviationId) {
      window.open(`/${attendanceAreaPathName}/${getCitizenLessonDeviationEditPath(citizenLessonDeviationId, citizenId)}`);
    }
  }

  const tooltip = (
    <Box>
      <Typography fontWeight="bold" fontSize="0.75rem">Bemærk</Typography>
      {notes.map(note => (
        <Typography key={note} fontSize="0.75rem" marginTop={1}>
          {note}
        </Typography>
      ))}
    </Box>
  );

  if (citizenLessonDeviationId) {
    return (
      <Tooltip title={tooltip} placement="top-start" slotProps={{ popper: { modifiers: [{ name: 'offset', options: { offset: [0, -8] } }] } }}>
        <IconButton aria-label="Vis særplan" size="small" onClick={handleOpenDeviation}>
          <FontAwesomeIcon icon={faExclamationCircle} color={ColorPalette.Crimson} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltip} placement="top-start" slotProps={{ popper: { modifiers: [{ name: 'offset', options: { offset: [0, -8] } }] } }}>
      <Box component="span" aria-label="Særlige forhold" role="img">
        <FontAwesomeIcon icon={faExclamationCircle} color={ColorPalette.Crimson} />
      </Box>
    </Tooltip>
  );
}
